import { useEffect, useRef, useState } from 'react'
import styles from './micpermissionsdialog.module.css'
import { Dispatch, SetStateAction } from 'react'
import * as Dialog from '@radix-ui/react-dialog'

interface PermissionsPopupProps {
    setMicPermissionsDenied: Dispatch<SetStateAction<boolean>>
    /** The mic was allowed after being refused, here or in the browser's settings.
     *  A caller that tried to record and could not should try again now. */
    onGranted?: () => void
    /** False to only watch the permission, with no dialog of its own: for a
     *  caller that asks for the mic straight away and explains a refusal itself. */
    showDialog?: boolean
}

export function PermissionsPopup({
    setMicPermissionsDenied,
    onGranted,
    showDialog = true,
}: PermissionsPopupProps) {
    // Read through a ref so the permission listener, bound once, calls the
    // caller's current handler rather than the one from the first render.
    const onGrantedRef = useRef(onGranted)
    onGrantedRef.current = onGranted

    const [openPermissionsModal, setOpenPermissionsModal] =
        useState<boolean>(false)

    useEffect(() => {
        const checkMicAccess = async () => {
            try {
                const permissionStatus = await navigator.permissions.query({
                    name: 'microphone' as PermissionName,
                })

                // `prompt` is not a refusal: the browser has not asked yet, or
                // was allowed "this time" only (Chrome), so it asks on every
                // visit. A caller that asks for the mic itself is about to
                // show that prompt, and treating it as refused said "Not
                // recording" over a capture that was recording.
                const refused = showDialog
                    ? permissionStatus.state !== 'granted'
                    : permissionStatus.state === 'denied'
                if (refused) {
                    setOpenPermissionsModal(true)
                    setMicPermissionsDenied(true)
                }
                // Allowed later, from the browser's prompt or its settings.
                permissionStatus.onchange = () => {
                    if (permissionStatus.state !== 'granted') return
                    setOpenPermissionsModal(false)
                    setMicPermissionsDenied(false)
                    onGrantedRef.current?.()
                }
            } catch (error) {
                console.error('Mic permission error:', error)
            }
        }
        checkMicAccess()
    }, [setMicPermissionsDenied, showDialog])

    const handlePermissionsModalClose = async () => {
        setOpenPermissionsModal(false)

        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: true,
            })

            setMicPermissionsDenied(false)

            stream.getTracks().forEach((track) => track.stop())
            onGrantedRef.current?.()
        } catch (error) {
            console.error('Error accessing microphone:', error)
            setMicPermissionsDenied(true)
        }
    }

    return (
        <Dialog.Root
            open={showDialog && openPermissionsModal}
            onOpenChange={setOpenPermissionsModal}
        >
            <Dialog.Portal>
                <Dialog.Overlay className={styles.overlay} />
                <Dialog.Content className={styles.content}>
                    <Dialog.Title className={styles.title}>
                        Microphone permissions
                    </Dialog.Title>
                    <Dialog.Description className={styles.description}>
                        The voice input button cannot be used unless microphone
                        permissions are allowed. After closing this popup, a
                        microphone permissions prompt from your browser will
                        appear. Please allow the microphone permissions if you
                        wish to use the voice input.
                    </Dialog.Description>
                    <div className={styles.actions}>
                        <button
                            className={styles.closeButton}
                            onClick={handlePermissionsModalClose}
                        >
                            Close
                        </button>
                    </div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    )
}

export default PermissionsPopup
